import axios from 'axios';
import router from '@/router';

const apiClient = axios.create({
  baseURL: '/api',
  // The production database is a remote MySQL host and measured responses
  // reach 3-18s under load (see REFACTORING_ROADMAP.md 2026-09-08). At the
  // previous 10s a slow reply aborted client-side mid-flight — including the
  // /csrf-token pre-flight below, which silently prevented the real POST from
  // ever being sent and surfaced as an unexplained "operation failed".
  timeout: 30000,
  withCredentials: true,
});

let csrfToken = null;
let csrfTokenRequest = null;

const csrfProtectedMethods = new Set(['post', 'put', 'patch', 'delete']);

async function getCsrfToken() {
  if (csrfToken) {
    return csrfToken;
  }

  if (!csrfTokenRequest) {
    csrfTokenRequest = apiClient.get('/csrf-token')
      .then(response => {
        csrfToken = response.data.csrf_token;
        return csrfToken;
      })
      .finally(() => {
        csrfTokenRequest = null;
      });
  }

  return csrfTokenRequest;
}

async function getErrorPayload(response) {
  const data = response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      return text ? JSON.parse(text) : null;
    } catch (_parseError) {
      return null;
    }
  }
  return data;
}

apiClient.interceptors.request.use(async config => {
  const method = (config.method || 'get').toLowerCase();
  if (csrfProtectedMethods.has(method)) {
    config.headers = config.headers || {};
    config.headers['X-CSRFToken'] = await getCsrfToken();
  }
  return config;
});

apiClient.interceptors.response.use(
  response => {
    if (response.config?.url === '/login' || response.config?.url === '/logout') {
      csrfToken = null;
    }
    return response;
  },
  async error => {
    const config = error.config;
    const errorPayload = await getErrorPayload(error.response);
    if (
      error.response
      && error.response.status === 400
      && errorPayload?.code === 'csrf_failed'
      && config
      && !config._csrfRetry
    ) {
      csrfToken = null;
      config._csrfRetry = true;
      return apiClient.request(config);
    }

    if (error.response && error.response.status === 401 && !config?.skipAuthRedirect) {
      // Not authenticated, redirect to login page
      router.push('/login');
    }
    return Promise.reject(error);
  }
);

export default apiClient;
